// src/components/Register.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import '../AuthStyles.css';
import { CATEGORY_OPTIONS } from '../constants/categories';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001').replace(/\/+$/, '');
const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// 追加：全角＠・．を半角へ、前後空白除去＆小文字化
const toAsciiEmail = (s) => String(s || '').replace(/[＠．]/g, (c) => (c === '＠' ? '@' : '.'));
const normEmail = (s) => toAsciiEmail(String(s || '').trim()).toLowerCase();

// 認証コードの有効期限（分）
const VERIFY_TTL_MIN = 10;

const hasLoc = (v) => v && typeof v.lat === 'number' && typeof v.lng === 'number';

const getCurrentPositionSmart = (opt1 = {}, opt2 = {}) =>
  new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) { reject(new Error('Geolocation not supported')); return; }
    const fast = { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000, ...opt1 };
    const high = { enableHighAccuracy: true,  timeout: 12000, maximumAge: 0,      ...opt2 };
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      () => {
        const id = navigator.geolocation.watchPosition(
          (pos2) => { navigator.geolocation.clearWatch(id); resolve(pos2); },
          (err2) => { setTimeout(() => { navigator.geolocation.clearWatch(id); reject(err2); }, 10000); },
          high
        );
      },
      fast
    );
  });

const buildFullAddress = ({ zipCode, prefecture, city, street, building }) => {
  const zip = zipCode ? `〒${zipCode} ` : '';
  const b = building ? ` ${building}` : '';
  return `${zip}${prefecture || ''}${city || ''}${street || ''}${b}`.trim();
};

const geocodeAddressSmart = async (address) => {
  if (window.google?.maps) {
    const geocoder = new window.google.maps.Geocoder();
    return new Promise((resolve, reject) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const loc = results[0].geometry.location;
          resolve({ lat: loc.lat(), lng: loc.lng() });
        } else {
          reject(new Error(`Geocode failed: ${status}`));
        }
      });
    });
  }
  if (!GOOGLE_KEY) throw new Error('Geocoding unavailable: API key missing');
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${encodeURIComponent(GOOGLE_KEY)}&language=ja`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Geocode HTTP ${res.status}`);
  const data = await res.json();
  if (data.status === 'OK' && data.results && data.results[0]) {
    const loc = data.results[0].geometry.location;
    return { lat: loc.lat, lng: loc.lng };
  }
  throw new Error(`Geocode failed: ${data.status}`);
};

const reverseGeocodeSmart = async ({ lat, lng }) => {
  if (window.google?.maps) {
    const geocoder = new window.google.maps.Geocoder();
    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) resolve(results[0]);
        else reject(new Error(`Reverse geocode failed: ${status}`));
      });
    });
  }
  if (!GOOGLE_KEY) throw new Error('Reverse geocoding unavailable: API key missing');
  const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${encodeURIComponent(GOOGLE_KEY)}&language=ja`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Reverse geocode HTTP ${res.status}`);
  const data = await res.json();
  if (data.status === 'OK' && data.results && data.results[0]) return data.results[0];
  throw new Error(`Reverse geocode failed: ${data.status}`);
};

const parseJpAddress = (result) => {
  const comps = result.address_components || [];
  const get = (t) => comps.find((c) => c.types.includes(t))?.long_name || '';
  const postal = get('postal_code');
  const pref   = get('administrative_area_level_1');
  const city   = get('locality') || get('sublocality_level_1') || get('administrative_area_level_2') || '';
  const parts = [
    get('sublocality_level_2'),
    get('sublocality_level_3'),
    get('sublocality_level_4'),
    get('sublocality_level_5'),
    get('route'),
    get('street_number'),
  ].filter(Boolean);
  const street = parts.join('');
  return { zipCode: postal, prefecture: pref, city, street };
};

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [ownerName, setOwnerName] = useState('');
  const [shopName, setShopName] = useState('');
  const [category, setCategory] = useState('');

  const [zipCode, setZipCode] = useState('');
  const [prefecture, setPrefecture] = useState('');
  const [city, setCity] = useState('');
  const [street, setStreet] = useState('');
  const [building, setBuilding] = useState('');

  const [shopLocation, setShopLocation] = useState({ lat: null, lng: null });

  // SNS・公式サイト（任意項目）
  const [officialWebsite, setOfficialWebsite] = useState('');
  const [officialInstagram, setOfficialInstagram] = useState('');
  const [officialFacebook, setOfficialFacebook] = useState('');
  const [officialX, setOfficialX] = useState('');

  const [verifyStep, setVerifyStep] = useState('idle'); // idle | code_sent | verified
  const [code, setCode] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [verifyStatus, setVerifyStatus] = useState(''); // '' | 'success' | 'fail'

  const [msg, setMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // URLパラメータをチェックしてStripe決済キャンセル時の処理
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const cancelled = urlParams.get('cancelled');
    const error = urlParams.get('error');
    const email = urlParams.get('email');
    
    if (cancelled === 'true') {
      setMsg('決済がキャンセルされました。登録を続行してください。');
      setTimeout(() => setMsg(''), 5000);
      // メールアドレスがURLパラメータにある場合は設定
      if (email) {
        setEmail(email);
      }
      // URLからパラメータを削除
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    } else if (error) {
      setMsg('決済処理でエラーが発生しました。再度お試しください。');
      setTimeout(() => setMsg(''), 5000);
      // URLからパラメータを削除
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location.search]);

  const onAddressChange = (setter) => (e) => {
    setter(e.target.value);
    if (hasLoc(shopLocation)) setShopLocation({ lat: null, lng: null });
  };

  // 認証コード送信
  const sendCode = async () => {
    setVerifyStatus('');
    const e = normEmail(email);
    if (!e) { setMsg('メールアドレスを入力してください'); return; }
    try {
      const res = await fetch(`${API_BASE}/auth/send-code`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify({ email: e }),
      });
      const data = await res.json().catch(()=> ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setVerifyStep('code_sent');
      setEmail(e); // 入力欄も正規化表示に揃える
    } catch (err) {
      console.error(err);
      setMsg('認証コードの送信に失敗しました');
      setTimeout(()=>setMsg(''), 3000);
    }
  };

  // 認証コード検証
  const verifyCode = async () => {
    const e = normEmail(email);
    if (!e || !code) { setMsg('コードを入力してください'); return; }
    try {
      const res = await fetch(`${API_BASE}/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify({ email: e, code }),
      });
      const data = await res.json().catch(()=> ({}));
      if (!res.ok || !data?.ok || !data?.token) throw new Error(data?.error || `HTTP ${res.status}`);
      setVerificationToken(data.token);
      setVerifyStep('verified');
      setVerifyStatus('success');
      setEmail(e);
    } catch (err) {
      console.error(err);
      setVerifyStatus('fail');
    }
  };

  const useCurrentLocation = async () => {
    try {
      const pos = await getCurrentPositionSmart();
      const ll = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setShopLocation(ll);

      try {
        const result = await reverseGeocodeSmart(ll);
        const { zipCode: z, prefecture: p, city: c, street: s } = parseJpAddress(result);
        if (z) setZipCode(z);
        if (p) setPrefecture(p);
        if (c) setCity(c);
        if (s) setStreet(s);
      } catch (re) {
        console.warn('Reverse geocoding failed:', re);
      }
    } catch (err) {
      console.warn('Geolocation failed:', err);
      setMsg('現在地を取得できませんでした。住所を入力してください。');
      setTimeout(()=>setMsg(''), 3000);
    }
  };


  async function startCheckout(createdId, email, API_BASE, setMsg) {
    try {
      if (!createdId && !email) {
        setMsg('店舗IDまたはメールが取得できませんでした');
        setTimeout(() => setMsg(''), 3000);
        return;
      }

      // body と query の両方に同じ値を入れる（どちらか読めなくても進む）
      const checkoutUrl =
        `${API_BASE}/billing/checkout` +
        `?shopId=${encodeURIComponent(createdId || '')}` +
        `&email=${encodeURIComponent(email || '')}`;

      const payload = { shopId: createdId || null, email: email || null };

      console.debug('[checkout] request', { checkoutUrl, payload });

      const res = await fetch(checkoutUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { raw: text }; }

      console.debug('[checkout] response', { status: res.status, data });

      if (!res.ok) {
        const msg = data?.message || data?.error || `HTTP ${res.status}`;
        throw new Error(msg);
      }
      if (!data?.url) throw new Error('Checkout URLが返ってきませんでした');

      // Stripe Checkoutへ遷移
      window.location.assign(data.url);
    } catch (err) {
      console.error('[checkout] error', err);
      setMsg(err?.message || '決済に進めませんでした');
      setTimeout(() => setMsg(''), 3000);
    }
  }


  const handleRegister = async () => {
    if (submitting) return;
    setSubmitting(true);

    if (!verificationToken) {
      setMsg('メール認証を完了してください');
      setTimeout(()=>setMsg(''), 2500);
      setSubmitting(false);
      return;
    }

    let loc = shopLocation;
    if (!hasLoc(loc)) {
      const address = buildFullAddress({ zipCode, prefecture, city, street, building });
      if (!address) {
        setMsg('住所を入力してください');
        setTimeout(()=>setMsg(''), 2500);
        setSubmitting(false);
        return;
      }
      try {
        loc = await geocodeAddressSmart(address);
        setShopLocation(loc);
      } catch (e) {
        console.error(e);
        setMsg('住所を正しく入力してください');
        setTimeout(()=>setMsg(''), 3000);
        setSubmitting(false);
        return;
      }
    }

    try {
      const e = normEmail(email);
      const res = await fetch(`${API_BASE}/shops/register`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify({
          email: e, password, ownerName, shopName,
          zipCode, prefecture, city, street, building,
          location: loc, category,
          officialWebsite, officialInstagram, officialFacebook, officialX,
          verificationToken,
        })
      });
      const data = await res.json().catch(()=> ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

      localStorage.setItem('authEmail', e);

      // 直後にCheckoutへ（失敗したら従来画面へフォールバック）
      try {
          const shopId =
            (data?.shop?._id) ||
            (data?._id) ||
            (data?.createdShopId);
  
          await startCheckout(shopId, e, API_BASE, setMsg);
          return; // Checkoutに遷移するのでここで抜ける
        } catch (err2) {
          console.warn('Checkout redirect failed:', err2);
          navigate(`/store-page?email=${encodeURIComponent(e)}`);
        }
    } catch (err) {
      console.error(err);
      const errorMsg = err?.message || '';
      // URLバリデーションエラーの場合は専用メッセージを表示
      if (errorMsg.includes('URLが無効') || errorMsg.includes('無効です') || errorMsg.includes('アクセスできません')) {
        setMsg('正しいURLを入力してください');
      } else {
        setMsg(errorMsg || '登録に失敗しました');
      }
      setTimeout(()=>setMsg(''), 3000);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:64px_64px]" />
      
      <div className="max-w-2xl mx-auto relative z-10">
        <div className="bg-card/80 backdrop-blur-md border border-border/50 rounded-3xl p-8 shadow-2xl ring-1 ring-white/10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h2 className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent text-3xl font-bold">
              新規店舗登録
            </h2>
            <p className="text-muted-foreground mt-2">
              ログインには <strong>メールアドレス</strong> と <strong>パスワード</strong> が必要です。必ず保管してください。
            </p>
          </div>

          <div className="space-y-6">
            {/* 認証（メール→コード→検証） */}
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                メールアドレス
              </label>
              <div className="flex gap-2">
                <input 
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
                  value={email} 
                  onChange={(e)=>setEmail(e.target.value)} 
                  placeholder="例）you@example.com" 
                />
                <button 
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors font-medium"
                  onClick={sendCode}
                >
                  コード送信
                </button>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                認証コードには<strong>有効期限（{VERIFY_TTL_MIN}分）</strong>があります。時間を過ぎたら再送信してください。
              </div>
            </div>

            {verifyStep !== 'idle' && (
              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  認証コード
                </label>
                <div className="flex gap-2">
                  <input 
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
                    value={code} 
                    onChange={(e)=>setCode(e.target.value)} 
                    placeholder="例）123456" 
                  />
                  <button 
                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={verifyCode} 
                    disabled={verifyStep === 'verified'}
                  >
                    {verifyStep === 'verified' ? '認証済み' : '認証する'}
                  </button>
                </div>
                {verifyStatus === 'success' && (
                  <div className="mt-2 text-green-600 text-sm">認証成功</div>
                )}
                {verifyStatus === 'fail' && (
                  <div className="mt-2 text-red-600 text-sm">認証コードが違います</div>
                )}
              </div>
            )}

        {/* 基本情報 */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-black mb-2">パスワード</label>
            <div className="relative flex w-full">
              <input
                className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors min-w-0"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e)=>setPassword(e.target.value)}
                placeholder="例）8文字以上の英数字"
              />
              <button
                type="button"
                className="flex-shrink-0 px-3 py-3 text-sm text-gray-600 hover:text-gray-800 transition-colors bg-gray-100 hover:bg-gray-200 rounded-r-lg border border-l-0 border-gray-300"
                onClick={()=>setShowPw(s => !s)}
                aria-label="パスワード表示切替"
                title="パスワード表示切替"
              >
                {showPw ? '隠す' : '表示'}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-black mb-2">オーナー名</label>
            <input 
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
              value={ownerName} 
              onChange={(e)=>setOwnerName(e.target.value)} 
              placeholder="例）山田 太郎" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-black mb-2">店舗名</label>
            <input 
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
              value={shopName} 
              onChange={(e)=>setShopName(e.target.value)} 
              placeholder="例）山田珈琲店" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-black mb-2">カテゴリ</label>
            <select 
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
              value={category} 
              onChange={(e)=>setCategory(e.target.value)}
            >
              <option value="">店舗の種類</option>
              {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* 住所 */}
        <div className="space-y-4" style={{ marginTop: 8 }}>
          <div>
            <label className="block text-sm font-medium text-black mb-2">郵便番号</label>
            <input 
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
              value={zipCode} 
              onChange={onAddressChange(setZipCode)} 
              placeholder="例）123-4567" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-black mb-2">都道府県</label>
            <input 
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
              value={prefecture} 
              onChange={onAddressChange(setPrefecture)} 
              placeholder="例）東京都" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-black mb-2">市区町村</label>
            <input 
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
              value={city} 
              onChange={onAddressChange(setCity)} 
              placeholder="例）千代田区" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-black mb-2">番地・丁目</label>
            <input 
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
              value={street} 
              onChange={onAddressChange(setStreet)} 
              placeholder="例）丸の内1-1-1" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-black mb-2">建物・部屋（任意）</label>
            <input 
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
              value={building} 
              onChange={onAddressChange(setBuilding)} 
              placeholder="例）丸の内ビル 10F" 
            />
          </div>
        </div>

        {/* SNS・公式サイト（任意項目） */}
        <div className="space-y-4" style={{ marginTop: 8 }}>
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              公式ホームページ <span className="text-gray-500 text-xs font-normal">(任意)</span>
            </label>
            <input 
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
              type="url"
              value={officialWebsite} 
              onChange={(e)=>setOfficialWebsite(e.target.value)} 
              placeholder="例）https://example.com" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              公式Instagram <span className="text-gray-500 text-xs font-normal">(任意)</span>
            </label>
            <input 
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
              type="url"
              value={officialInstagram} 
              onChange={(e)=>setOfficialInstagram(e.target.value)} 
              placeholder="例）https://instagram.com/yourshop" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              公式Facebook <span className="text-gray-500 text-xs font-normal">(任意)</span>
            </label>
            <input 
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
              type="url"
              value={officialFacebook} 
              onChange={(e)=>setOfficialFacebook(e.target.value)} 
              placeholder="例）https://facebook.com/yourshop" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              公式X（Twitter） <span className="text-gray-500 text-xs font-normal">(任意)</span>
            </label>
            <input 
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-colors" 
              type="url"
              value={officialX} 
              onChange={(e)=>setOfficialX(e.target.value)} 
              placeholder="例）https://x.com/yourshop" 
            />
          </div>
        </div>

        {/* 現在地ボタン */}
        <div className="auth-actions">
          <button type="button" className="btn-ghost" onClick={useCurrentLocation}>現在地を使う</button>
          <div className="auth-actions-right">
            {/* ▼ここだけ変更 */}
            <button className="btn-primary" onClick={handleRegister} disabled={submitting}>
              {submitting ? '処理中…' : '¥300/月で公開する'}
            </button>

            <div className="text-center">
              <Link 
                to="/login" 
                className="text-purple-600 hover:text-purple-700 font-medium transition-colors"
              >
                ログインへ戻る
              </Link>
            </div>
          </div>
        </div>

        {msg && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-600 px-4 py-3 rounded-lg mt-6">
            {msg}
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
